import { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, Users, RefreshCw } from 'lucide-react';
import { Card, Button } from '../../components/WorkflowFormPrimitives';
import { VoteTallyRow } from '../../../../services/ApprovalEnvelopeApiClient';
import { useVoteTally } from './useVoteTally';

interface Props {
  analysisId: string | null;
  runId: string | null;
  /** Bump to refetch tally (e.g. after Vote Room activity). */
  refreshKey?: number;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

export function VoteTallyBanner({ analysisId, runId, refreshKey = 0 }: Props) {
  const { tally, loading, error, retry } = useVoteTally(analysisId, runId);
  const [localRefresh, setLocalRefresh] = useState(0);

  useEffect(() => {
    if (refreshKey > 0) setLocalRefresh((k) => k + 1);
  }, [refreshKey]);

  useEffect(() => {
    if (localRefresh > 0) retry();
  }, [localRefresh, retry]);

  if (!analysisId) return null;

  if (loading && !tally) {
    return (
      <Card>
        <div className="border border-gray-200 bg-gray-50/40 rounded-lg p-4" aria-busy="true">
          <p className="text-xs text-gray-500">Loading shareholder approval status…</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="border border-red-200 bg-red-50/40 rounded-lg p-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
          <Button variant="secondary" size="sm" className="shrink-0" onClick={() => retry()}>
            <RefreshCw size={12} className="mr-1" />
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  if (!tally || tally.totalShares === 0) return null;

  const accent = tally.thresholdMet
    ? 'border-emerald-200 bg-emerald-50/40'
    : tally.unvotedShares > 0
      ? 'border-amber-200 bg-amber-50/40'
      : 'border-red-200 bg-red-50/40';

  const Icon = tally.thresholdMet ? CheckCircle2 : tally.unvotedShares > 0 ? Users : AlertTriangle;
  const iconColor = tally.thresholdMet
    ? 'text-emerald-600'
    : tally.unvotedShares > 0
      ? 'text-amber-600'
      : 'text-red-600';

  return (
    <Card>
      <div className={`border ${accent} rounded-lg p-4`}>
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-white border border-gray-200 flex items-center justify-center">
            <Icon size={18} className={iconColor} aria-hidden />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xs font-medium text-gray-900">Shareholder Approval Status</h3>
              <span
                className={`text-[10px] uppercase tracking-wide font-semibold ${
                  tally.thresholdMet ? 'text-emerald-700' : 'text-gray-500'
                }`}
              >
                {tally.thresholdMet ? 'Threshold Met' : 'Threshold Not Yet Met'}
              </span>
            </div>
            <p className="text-xs text-gray-600" aria-live="polite">
              {formatNumber(tally.approveShares)} of {formatNumber(tally.eligibleShares)} eligible voting shares have
              approved (
              <span className={tally.thresholdMet ? 'text-emerald-700 font-medium' : 'text-gray-800 font-medium'}>
                {formatPercent(tally.approvalRatio)}
              </span>
              ). Threshold is more than {formatPercent(tally.thresholdRatio)} per §280G(b)(5)
              {tally.requiredApproveSharesForApproval > 0 ? (
                <> ({formatNumber(tally.requiredApproveSharesForApproval)} approve shares required)</>
              ) : null}
              .
            </p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
              <Stat label="Approve" value={tally.approveShares} variant="success" />
              <Stat label="Disapprove" value={tally.disapproveShares} variant="danger" />
              <Stat label="Abstain" value={tally.abstainShares} variant="muted" />
              <Stat label="Outstanding" value={tally.unvotedShares} variant="muted" />
            </div>
            {tally.outstandingShareholders.length > 0 && !tally.thresholdMet ? (
              <div className="mt-2 text-[11px] text-gray-500">
                Outstanding:{' '}
                {tally.outstandingShareholders
                  .slice(0, 3)
                  .map((s) => s.name)
                  .join(', ')}
                {tally.outstandingShareholders.length > 3 ? ` +${tally.outstandingShareholders.length - 3} more` : ''}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value, variant }: { label: string; value: number; variant: 'success' | 'danger' | 'muted' }) {
  const color = variant === 'success' ? 'text-emerald-700' : variant === 'danger' ? 'text-red-700' : 'text-gray-700';
  return (
    <div className="bg-white border border-gray-200 rounded p-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`font-semibold tabular-nums ${color}`}>{value.toLocaleString()}</div>
    </div>
  );
}
